package nl.staartvin.solarpower.data.chart

import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import nl.staartvin.solarpower.data.model.UvDay
import nl.staartvin.solarpower.data.model.UvPoint
import nl.staartvin.solarpower.data.util.interpolateUvi
import kotlin.math.ceil
import kotlin.math.max
import kotlin.time.Duration.Companion.hours

/**
 * One vertical hour marker (7am..7pm). Renderers draw a line from the x-axis
 * up to where the forecast curve sits at this hour.
 */
data class HourMarker(val timeMillis: Long, val uvi: Double, val hour: Int)

/**
 * Snapshot returned by the scrub handle. Carries the snapped time in
 * primitive form (epoch millis, hour, minute) so Swift never has to touch the
 * mangled `Kotlinx_datetimeInstant` symbol.
 */
data class ChartReadout(
    val timeMillis: Long,
    val uvi: Double,
    val hour: Int,
    val minute: Int,
) {
    val timeLabel: String
        get() = "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
}

/**
 * Everything the chart renderers need, packaged for them.
 *
 * Lives in commonMain so Compose and SwiftUI use the *same* axis math and band
 * boundaries — the only thing each platform writes is "given a (x, y) pixel
 * coordinate, draw something there."
 *
 * [now] is nullable because a chart pinned to a specific day (e.g. "Tomorrow")
 * shouldn't draw a "now" marker — the current moment is outside the viewport.
 *
 * [timeZone] is the device's system timezone. Both the day boundary and all
 * label rendering use it, so the chart matches the user's wall clock at their
 * actual location.
 *
 * Trade-off: when faking GPS to a distant location, the chart's labels remain
 * in the device's timezone (the data is for the distant location, the labels
 * are in your time). A proper fix would map lat/lon → IANA timezone via an
 * offline tzdb or geocoder; left as a follow-up.
 */
data class UvChartData(
    val startTime: Instant,
    val endTime: Instant,
    val yMax: Double,
    val forecast: List<UvPoint>,
    val measured: List<UvPoint>,
    val now: UvPoint?,
    val timeZone: TimeZone,
) {
    /** Converts an Instant in [[startTime, endTime]] to a fraction in [0, 1]. */
    fun normalizeTime(t: Instant): Double = normalizeTimeMillis(t.toEpochMilliseconds())

    /**
     * Convenience for Swift callers that work in epoch-millis to avoid the
     * mangled `Kotlinx_datetimeInstant` Swift symbol. Identical math.
     */
    fun normalizeTimeMillis(millis: Long): Double {
        val total = (endTime.toEpochMilliseconds() - startTime.toEpochMilliseconds()).toDouble()
        if (total <= 0.0) return 0.0
        val pos = (millis - startTime.toEpochMilliseconds()).toDouble()
        return (pos / total).coerceIn(0.0, 1.0)
    }

    val startTimeMillis: Long get() = startTime.toEpochMilliseconds()
    val endTimeMillis: Long get() = endTime.toEpochMilliseconds()

    /**
     * Boundary between "real" past data and "predicted" future data — used by
     * renderers to draw forecast segments dashed.
     *
     * Defaults to [now]'s time. When [now] is null (e.g. the Tomorrow tab where
     * the current moment is outside the viewport), every segment is in the
     * future, so we use [Long.MIN_VALUE] to make the entire curve dashed.
     */
    val measuredCutoffMillis: Long
        get() = now?.time?.toEpochMilliseconds() ?: Long.MIN_VALUE

    /** Converts a UVI value in [0, yMax] to a fraction in [0, 1]. */
    fun normalizeUvi(uvi: Double): Double = (uvi / yMax).coerceIn(0.0, 1.0)

    /**
     * Combined, time-sorted, deduplicated past+future samples. Computed once
     * so interpolation (uviAtMillis) and downstream callers see one curve.
     *
     * `measured` is listed first so [distinctBy] keeps the satellite-observed
     * value (the "what really happened") over RIVM's forecast prediction for
     * overlapping past times. For currentuvindex.com the two lists only
     * overlap at `now`, where the value is the same either way.
     */
    val allSamples: List<UvPoint> =
        (measured + forecast).distinctBy { it.time }.sortedBy { it.time }

    /**
     * The highest-UVI sample on this chart. Used by the UI to surface a
     * "Today's peak: 7.2 at 13:45" line so the user can see the day's max
     * without reading the curve. `null` only on an empty chart.
     */
    val peakSample: UvPoint? = allSamples.maxByOrNull { it.uvi }

    /**
     * "Now" as the user means it: the device's current clock time, with UVI
     * interpolated from [allSamples]. Distinct from [now], which is the latest
     * *actual data point* — that lags real time (satellite/processing delay for
     * RIVM, hourly snapshots for currentuvindex), so anchoring the "now" marker
     * and the scrub handle to it confused users ("it's 15:15 but the line is at
     * 14:30"). This points them at the real moment instead. UVI past the last
     * measurement comes from the forecast curve (drawn dashed), which is honest.
     *
     * `null` when the current time is outside this chart's window — e.g. the
     * Tomorrow tab, or late at night past the trimmed viewport — mirroring how
     * [now] is null there.
     *
     * [now] is deliberately still used for [measuredCutoffMillis] (the
     * measured-vs-forecast boundary), which really is the last measurement.
     */
    val currentPoint: UvPoint? = run {
        val nowMs = Clock.System.now().toEpochMilliseconds()
        if (nowMs < startTimeMillis || nowMs > endTimeMillis) return@run null
        val uvi = interpolateUvi(allSamples, nowMs) ?: return@run null
        UvPoint(Instant.fromEpochMilliseconds(nowMs), uvi)
    }

    /** Linear interpolation of UVI at [millis] across [allSamples]. */
    fun uviAtMillis(millis: Long): Double? = interpolateUvi(allSamples, millis)

    /**
     * Vertical hour markers between 7am and 7pm (in the chart's [timeZone])
     * that have interpolatable forecast data.
     */
    fun hourMarkers(): List<HourMarker> {
        val date = startTime.toLocalDateTime(timeZone).date
        return (7..19).mapNotNull { hour ->
            val t = LocalDateTime(date, LocalTime(hour, 0)).toInstant(timeZone)
            val ms = t.toEpochMilliseconds()
            if (ms < startTimeMillis || ms > endTimeMillis) return@mapNotNull null
            val uvi = uviAtMillis(ms) ?: return@mapNotNull null
            HourMarker(timeMillis = ms, uvi = uvi, hour = hour)
        }
    }

    /**
     * Convert a chart x-fraction (0..1) to a scrubber readout, snapped to the
     * nearest 30 minutes. Returns null when no forecast data is interpolatable
     * at the snapped time. Time labels are formatted in [timeZone].
     */
    fun readoutAtFraction(fraction: Double): ChartReadout? {
        val totalMs = endTimeMillis - startTimeMillis
        val rawMs = startTimeMillis + (totalMs * fraction.coerceIn(0.0, 1.0)).toLong()
        val snapMs = 30L * 60L * 1000L
        val snappedMs = ((rawMs + snapMs / 2) / snapMs) * snapMs
        val finalMs = snappedMs.coerceIn(startTimeMillis, endTimeMillis)
        val uvi = uviAtMillis(finalMs) ?: return null
        val local = Instant.fromEpochMilliseconds(finalMs).toLocalDateTime(timeZone)
        return ChartReadout(timeMillis = finalMs, uvi = uvi, hour = local.hour, minute = local.minute)
    }

    companion object {
        /**
         * Chart pinned to one local calendar day (midnight to next midnight in
         * [zone]). [zone] is used for both the day boundary AND label rendering.
         */
        fun forLocalDay(day: UvDay, date: LocalDate, zone: TimeZone): UvChartData {
            val dayStart = date.atStartOfDayIn(zone)
            val dayEnd = date.plus(1, DateTimeUnit.DAY).atStartOfDayIn(zone)
            val inDay = { t: Instant -> t >= dayStart && t < dayEnd }

            val forecast = day.forecast.filter { inDay(it.time) }
            val measured = day.measured.filter { inDay(it.time) }
            val peak = (forecast + measured).maxOfOrNull { it.uvi } ?: 0.0
            // Tight y-axis: round up to the next integer above peak (matches
            // RIVM website's display) with a small headroom and a floor of 4
            // so the curve never looks flat on a cloudy day.
            val yMax = max(ceil(peak), max(peak + 0.5, 4.0))

            // Trim the x-axis to the actually-interesting window: from 2h before
            // the first VISIBLE UVI to 2h after the last. Clamped to
            // dayStart/dayEnd so we never escape the requested day.
            //
            // Filter at MIN_VISIBLE_UVI rather than strictly > 0 because RIVM's
            // forecast bands report fractional values (0.013, 0.021, …) starting
            // from civil twilight — non-zero but below 1 pixel of vertical
            // resolution on a typical chart. Including them adds an "invisible
            // tail" of viewport on the left that varies by data source
            // (RIVM has it, currentuvindex's hourly bands don't), producing
            // visibly different chart widths between Today and Tomorrow tabs.
            val all = (forecast + measured).asSequence().filter { it.uvi >= MIN_VISIBLE_UVI }
            val firstActive = all.minByOrNull { it.time }?.time
            val lastActive = all.maxByOrNull { it.time }?.time
            val viewStart = firstActive?.let { maxOf(dayStart, it - 2.hours) } ?: dayStart
            val viewEnd = lastActive?.let { minOf(dayEnd, it + 2.hours) } ?: dayEnd

            return UvChartData(
                startTime = viewStart,
                endTime = viewEnd,
                yMax = yMax,
                forecast = forecast,
                measured = measured,
                now = day.now.takeIf { it.time in viewStart..viewEnd },
                timeZone = zone,
            )
        }

        /**
         * Chart for "today + offset" in the device's timezone. `0` is today,
         * `1` is tomorrow. The day boundary AND labels both follow the device,
         * so "13:00" on the chart matches the user's phone clock at their
         * actual location.
         *
         * For faked GPS far from the device, labels stay in the device's
         * timezone — known limitation; resolving it cleanly needs lat/lon →
         * IANA timezone lookup.
         */
        fun forRelativeDay(day: UvDay, dayOffset: Int): UvChartData {
            val zone = TimeZone.currentSystemDefault()
            val today = Clock.System.now().toLocalDateTime(zone).date
            return forLocalDay(day, today.plus(dayOffset, DateTimeUnit.DAY), zone)
        }

        /**
         * UVI below this value is sub-pixel on a typical chart (peak 7,
         * height 186dp → 1 pixel ≈ 0.038 UVI). Used as the threshold for
         * "first visible sample" when deciding chart trim boundaries, so
         * data sources with finer granularity (RIVM 15-min) don't appear
         * to start earlier than coarser ones (currentuvindex hourly).
         */
        private const val MIN_VISIBLE_UVI = 0.1
    }
}
